import { format, isValid, parse } from "date-fns";
import { Calendar as CalendarIcon, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Calendar } from "./calendar";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "./drawer";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { formatIsoDate } from "@/lib/format";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  /** ISO `YYYY-MM-DD`, or empty string when unset. Matches the same wire
   *  format our forms use everywhere — swap in place of `<Input type="date">`
   *  without converting values. */
  value: string;
  onChange: (next: string) => void;
  /** Label when no date is selected. Defaults to "Pick a date". */
  placeholder?: string;
  /** Size preset — `default` matches `<Input>` height (36px), `sm` matches
   *  the small form-button size (32px). */
  size?: "default" | "sm";
  /** Renders an X to clear the selection when a value is set. Defaults to
   *  true — most forms treat dates as optional. Pass `false` for required
   *  fields where the clear affordance would be confusing. */
  clearable?: boolean;
  /** ISO `YYYY-MM-DD`. Earliest allowable date — dates before this are
   *  disabled in the calendar. Empty string means no lower bound. */
  minDate?: string;
  /** ISO `YYYY-MM-DD`. Latest allowable date — dates after this are
   *  disabled. Empty string means no upper bound. */
  maxDate?: string;
  disabled?: boolean;
  id?: string;
  className?: string;
}

const DISPLAY_FORMAT = "MMM d, yyyy";

// Try the most common typed formats. ISO comes first because users who paste
// from spreadsheets/logs typically have YYYY-MM-DD on their clipboard; M/d
// covers "1/5/2027" and full-month names cover "January 5, 2027" pastes.
const PARSE_FORMATS = [
  "yyyy-MM-dd",
  "M/d/yyyy",
  "MM/dd/yyyy",
  "M/d/yy",
  DISPLAY_FORMAT,
  "MMM d yyyy",
  "MMMM d, yyyy",
  "MMMM d yyyy",
];

function tryParse(text: string): Date | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  for (const f of PARSE_FORMATS) {
    const d = parse(trimmed, f, new Date());
    if (isValid(d)) return d;
  }
  return null;
}

/**
 * Single-date picker. The trigger is a typable input — users can either pick
 * from the popover calendar (desktop) / drawer (mobile) or type a date
 * directly ("1/5/2027", "Jan 5, 2027", "2027-01-05"). Manual entry exists so
 * users picking dates far from today don't have to month-click through a
 * calendar.
 *
 * The value is an ISO `YYYY-MM-DD` string (or empty) so callers can pass it
 * straight through to server DTOs without conversion. The internal `Date`
 * representation never leaks.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = "Pick a date",
  size = "default",
  clearable = true,
  minDate,
  maxDate,
  disabled,
  id,
  className,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  // sm breakpoint = 640 px. Below that we use a bottom-sheet Drawer.
  const isMobile = !useMediaQuery("(min-width: 640px)");

  // Memoised on the STRING, not recomputed per render.
  //
  // This ran three times on every render of every picker on the page,
  // including renders driven by a sibling's keystroke where none of the
  // inputs changed. Keyed on the `yyyy-MM-dd` strings the props already are,
  // so the memo hits whenever the date has not actually changed.
  //
  // Housekeeping rather than a measured win: profiling the surface that
  // prompted it (the Market Intelligence strip controls, which render two
  // pickers) showed no difference from this alone. Kept because repeating the
  // work is still wrong, not because it fixed anything.
  const parsedDate = useMemo(
    () => (value ? parse(value, "yyyy-MM-dd", new Date()) : undefined),
    [value],
  );
  const parsedMin = useMemo(
    () => (minDate ? parse(minDate, "yyyy-MM-dd", new Date()) : undefined),
    [minDate],
  );
  const parsedMax = useMemo(
    () => (maxDate ? parse(maxDate, "yyyy-MM-dd", new Date()) : undefined),
    [maxDate],
  );

  // Mirror `value` into the input's text. When the parent value changes
  // (calendar pick, external reset), update what the user sees. While the
  // user is typing, `inputText` may diverge from `value` until blur.
  const [inputText, setInputText] = useState(
    parsedDate ? format(parsedDate, DISPLAY_FORMAT) : "",
  );
  const lastValueRef = useRef(value);
  useEffect(() => {
    if (lastValueRef.current === value) return;
    lastValueRef.current = value;
    setInputText(parsedDate ? format(parsedDate, DISPLAY_FORMAT) : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commitText = () => {
    if (!inputText.trim()) {
      if (value) onChange("");
      return;
    }
    const d = tryParse(inputText);
    if (d) {
      // Respect min/max — silently snap back if the typed date is out of
      // range. Surfacing a validation error here is a job for the form
      // schema; the picker just refuses to commit.
      if (parsedMin && d < parsedMin) {
        setInputText(parsedDate ? format(parsedDate, DISPLAY_FORMAT) : "");
        return;
      }
      if (parsedMax && d > parsedMax) {
        setInputText(parsedDate ? format(parsedDate, DISPLAY_FORMAT) : "");
        return;
      }
      const iso = formatIsoDate(d);
      onChange(iso);
      setInputText(format(d, DISPLAY_FORMAT));
    } else {
      // Unparseable — revert the visible text to the last committed value.
      setInputText(parsedDate ? format(parsedDate, DISPLAY_FORMAT) : "");
    }
  };

  const onSelect = (date: Date | undefined) => {
    if (date) {
      onChange(formatIsoDate(date));
      setInputText(format(date, DISPLAY_FORMAT));
      setOpen(false);
    } else {
      onChange("");
      setInputText("");
    }
  };

  const onClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange("");
    setInputText("");
  };

  const inputId = id;
  const inputHeight = size === "sm" ? "h-8" : "h-9";

  // The typable input. Lives outside the popover/drawer so the user can edit
  // without opening the calendar. `className` lands on the Input so callers
  // that ship width/height tweaks (`h-8 min-w-[130px]`) still apply.
  const field = (
    <div className="relative w-full">
      <Input
        id={inputId}
        type="text"
        value={inputText}
        onChange={(e) => setInputText(e.target.value)}
        onBlur={commitText}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commitText();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(inputHeight, "pr-16", className)}
      />
      <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center gap-0.5">
        {clearable && value && !disabled && (
          <button
            type="button"
            aria-label="Clear date"
            onClick={onClear}
            className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-row-hover hover:text-foreground"
          >
            <X size={14} />
          </button>
        )}
        {isMobile ? (
          <DrawerTrigger asChild>
            <button
              type="button"
              aria-label="Open calendar"
              disabled={disabled}
              className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-row-hover hover:text-foreground disabled:opacity-50"
            >
              <CalendarIcon size={14} />
            </button>
          </DrawerTrigger>
        ) : (
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-label="Open calendar"
              disabled={disabled}
              className="flex h-6 w-6 items-center justify-center rounded-sm text-muted-foreground hover:bg-row-hover hover:text-foreground disabled:opacity-50"
            >
              <CalendarIcon size={14} />
            </button>
          </PopoverTrigger>
        )}
      </div>
    </div>
  );

  // react-day-picker accepts an array of matchers on `disabled`.
  const disabledMatchers = [
    ...(parsedMin ? [{ before: parsedMin }] : []),
    ...(parsedMax ? [{ after: parsedMax }] : []),
  ];

  /**
   * Built only while the picker is OPEN.
   *
   * Radix does not mount a closed `PopoverContent`'s children, but the element
   * tree was still being CONSTRUCTED on every render — a full
   * `react-day-picker` element each time, for a calendar nobody had asked to
   * see. A function rather than a hoisted `const`, so that cost is paid on
   * open instead.
   *
   * Housekeeping rather than a measured win, same as the parse memo above:
   * the surface that prompted it did not get faster from this alone.
   */
  const renderCalendar = () => (
    <Calendar
      fluid={isMobile}
      mode="single"
      numberOfMonths={1}
      selected={parsedDate}
      onSelect={onSelect}
      autoFocus
      defaultMonth={parsedDate}
      disabled={disabledMatchers.length > 0 ? disabledMatchers : undefined}
    />
  );

  // Hand-render Variant: Button-only `subtle` trigger when the parent passes
  // a className that explicitly hides the text input. (Not currently needed —
  // every existing caller is happy with the input field.)

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        {field}
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>Pick a date</DrawerTitle>
          </DrawerHeader>
          <div className="px-2 pb-4">{open && renderCalendar()}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {field}
      <PopoverContent className="w-auto p-0" align="start">
        {open && renderCalendar()}
      </PopoverContent>
    </Popover>
  );
}
